import React, { useState, useCallback } from 'react';
import { DataProvider, useData } from './context/DataContext';
import ViewTabs from './components/Controls/ViewTabs';
import PanelSelector from './components/Controls/PanelSelector';
import GeneSearch from './components/Controls/GeneSearch';
import ColumnToggle from './components/Controls/ColumnToggle';
import DotPlot from './components/DotPlot/DotPlot';
import StripPlot from './components/StripPlot/StripPlot';
import GeneDetail from './components/GeneDetail/GeneDetail';
import VolcanoPlot from './components/Volcano/VolcanoPlot';
import MutationView from './components/Mutations/MutationView';
import TransformationView from './components/Transformation/TransformationView';
import ReportControls from './components/Report/ReportControls';
import ReportBuilder from './components/Report/ReportBuilder';
import './App.css';

function PasswordGate({ children }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input === 'klinelab') {
      setAuthenticated(true);
    } else {
      setError(true);
      setInput('');
    }
  };

  if (authenticated) return children;

  return (
    <div className="password-gate">
      <div className="password-box">
        <h2>Murine B-Cell Expression Explorer</h2>
        <p>This tool is restricted to lab members.</p>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={input}
            onChange={e => { setInput(e.target.value); setError(false); }}
            placeholder="Enter password"
            autoFocus
          />
          <button type="submit">Enter</button>
        </form>
        {error && <p className="password-error">Incorrect password</p>}
      </div>
    </div>
  );
}

function AppContent() {
  const { data, loading, error, visibleGroups, setVisibleGroups } = useData();
  const [activeView, setActiveView] = useState('dotplot');
  const [selectedPanel, setSelectedPanel] = useState('GC Polarization');
  const [selectedGene, setSelectedGene] = useState(null);
  const [colorMode, setColorMode] = useState('relative');
  const [reportGenes, setReportGenes] = useState([]);
  const [reportColorMode, setReportColorMode] = useState('relative');

  const handleSelectGene = useCallback((gene) => {
    setSelectedGene(gene);
    setActiveView('gene');
  }, []);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        <p>Loading expression data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <h2>Error loading data</h2>
        <p>{error}</p>
        <p>Make sure to run <code>python scripts/export_data.py</code> first.</p>
      </div>
    );
  }

  // Show column toggle for views that use the expression matrix
  const showColumnToggle = ['dotplot', 'strip', 'gene', 'report'].includes(activeView);
  const showGeneSearch = !['report', 'volcano', 'mutations', 'transformation'].includes(activeView);

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <h1>Murine B-Cell Expression Explorer</h1>
          <p className="subtitle">
            ImmGen normal B cells | A20 lymphoma | EmBO GEMM (EZH2-Y641F/BCL2)
          </p>
        </div>
      </header>

      <main className="app-main">
        <aside className="controls-panel">
          <ViewTabs activeView={activeView} onChangeView={setActiveView} />

          {(activeView === 'dotplot' || activeView === 'strip') && (
            <PanelSelector
              selectedPanel={selectedPanel}
              onSelectPanel={setSelectedPanel}
            />
          )}

          {activeView === 'dotplot' && (
            <div className="color-toggle">
              <label>Color Mode</label>
              <div className="toggle-buttons">
                <button
                  className={colorMode === 'relative' ? 'active' : ''}
                  onClick={() => setColorMode('relative')}
                >
                  Z-score
                </button>
                <button
                  className={colorMode === 'absolute' ? 'active' : ''}
                  onClick={() => setColorMode('absolute')}
                >
                  Absolute
                </button>
              </div>
            </div>
          )}

          {showColumnToggle && (
            <ColumnToggle
              visibleGroups={visibleGroups}
              setVisibleGroups={setVisibleGroups}
            />
          )}

          {activeView === 'report' && (
            <ReportControls
              reportGenes={reportGenes}
              setReportGenes={setReportGenes}
              reportColorMode={reportColorMode}
              setReportColorMode={setReportColorMode}
            />
          )}

          {showGeneSearch && (
            <GeneSearch onSelectGene={handleSelectGene} />
          )}

          <div className="data-info">
            <h4>Data Sources</h4>
            <ul>
              <li><strong>ImmGen</strong> — Yoshida '19</li>
              <li><strong>A20 vitro</strong> — Chan '22 (n=2)</li>
              <li><strong>A20 vivo</strong> — Lightcap '21 (n=6)</li>
              <li><strong>GCB_WT</strong> — Isshiki '25 (n=5)</li>
              <li><strong>GCB_BCL2</strong> — Isshiki '25 (n=5)</li>
              <li><strong>GCB_Ezh2</strong> — Isshiki '25 (n=4)</li>
              <li><strong>P0 (Ezh2/BCL2)</strong> — Isshiki '25 (n=5)</li>
              <li><strong>P6</strong> — Isshiki '25 (n=3)</li>
              <li><strong>P6+Taz</strong> — Isshiki '25 (n=3)</li>
            </ul>
            <p className="gene-count">{data?.geneList?.length?.toLocaleString()} genes loaded</p>
          </div>
        </aside>

        <section className="visualization-panel">
          {activeView === 'dotplot' && (
            <DotPlot panelName={selectedPanel} colorMode={colorMode} />
          )}
          {activeView === 'strip' && (
            <StripPlot panelName={selectedPanel} />
          )}
          {activeView === 'volcano' && (
            <VolcanoPlot onSelectGene={handleSelectGene} />
          )}
          {activeView === 'mutations' && (
            <MutationView onSelectGene={handleSelectGene} />
          )}
          {activeView === 'transformation' && (
            <TransformationView onSelectGene={handleSelectGene} />
          )}
          {activeView === 'gene' && (
            <GeneDetail gene={selectedGene} onSelectGene={handleSelectGene} />
          )}
          {activeView === 'report' && (
            <ReportBuilder reportGenes={reportGenes} reportColorMode={reportColorMode} />
          )}
        </section>
      </main>

      <footer className="app-footer">
        <span>Murine B-Cell Expression Explorer <span className="version">v0.2</span></span>
        <span className="sep">|</span>
        <span>Data: ImmGen, GEO (GSE203162, GSE173114, GSE255548), Kline Lab WGS</span>
      </footer>
    </div>
  );
}

function App() {
  return (
    <PasswordGate>
      <DataProvider>
        <AppContent />
      </DataProvider>
    </PasswordGate>
  );
}

export default App;
